import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Slide,
  CircularProgress,
  MenuItem,
  Select,
  Avatar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TextField } from "@mui/material";
import { LanguageContext } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import TicketActivityHistory from "../components/TicketActivityHistory";
import { statusColors } from "../constants/statusColors";
import UploadFilesModal from "../uploaders/UploadFilesModal";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteDoc, doc, getDocs, collection } from "firebase/firestore";
import { ref as storageRef, listAll, deleteObject } from "firebase/storage";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import { db, storage } from "../firebase";
import { useTheme } from "@mui/material/styles";

const EditTicketModal = ({
  open,
  onClose,
  ticket,
  handleUpdate,
  loading,
  role,
  selectedProject,
  attachments: parentAttachments,
  setAttachments: setParentAttachments,
}) => {
  const { translate } = useContext(LanguageContext);
  const [ticketName, setTicketName] = useState(ticket?.ticketName);
  const [ticketType, setTicketType] = useState(ticket?.ticketType);
  const [originalQuestion, setOriginalQuestion] = useState(
    ticket?.originalQuestion
  );
  const [pageList, setPageList] = useState(ticket?.pageList);
  const [selectedPriority, setSelectedPriority] = useState(ticket?.priority);
  const { currentUser } = useAuth();
  const [ticketStatus, setTicketStatus] = useState(ticket?.status || "Open");
  const [newComment, setNewComment] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [localAttachments, setLocalAttachments] = useState([]);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState("");
  const theme = useTheme();
  const PRIORITY_BG = {
    Low: theme.palette.success.main,
    Medium: theme.palette.warning.main,
    High: theme.palette.error.main,
  };

  // Compare l’état local aux props du ticket pour savoir s’il y a eu modification

  useEffect(() => {
    if (ticket) {
      setTicketName(ticket.ticketName);
      setTicketType(ticket.ticketType);
      setOriginalQuestion(ticket.originalQuestion);
      setPageList(ticket.pageList);
      setSelectedPriority(ticket.priority);
      setTicketStatus(ticket.status);
      setLocalAttachments(
        (ticket.images || []).map((url) => ({
          url,
          file: null,
          progress: 100,
          uploaded: true,
        }))
      );
    }
  }, [ticket]);

  if (!ticket) return null;

  const initialImages = (ticket.images || []).map((url) => ({
    url,
    file: null,
    progress: 100,
    uploaded: true,
  }));
  const hasChanges =
    ticketName !== ticket.ticketName ||
    ticketType !== ticket.ticketType ||
    originalQuestion !== ticket.originalQuestion ||
    pageList !== ticket.pageList ||
    selectedPriority !== ticket.priority ||
    ticketStatus !== ticket.status ||
    // on compare simplement la longueur des listes d’images (ou stringify si besoin)
    localAttachments.length !== initialImages.length;

  const status = ticket?.status; // Define the ticket status

  // Move this part outside the return block

  const statusOptions = [
    { value: "Open", label: translate("Open"), color: "#28a745" },
    {
      value: "Working on It",
      label: translate("WorkingOnIt"),
      color: "#17a2b8",
    },
    { value: "In Staging", label: translate("InStaging"), color: "#ffc107" },
    { value: "In Test", label: translate("InTest"), color: "#fd7e14" },
    {
      value: "In Production",
      label: translate("InProduction"),
      color: "#6610f2",
    },
    { value: "In review", label: translate("InReview"), color: "#6c757d" },
    { value: "Closed", label: translate("Closed"), color: "#dc3545" },
  ];

  const currentStatusColor = statusColors[ticketStatus] || "#ccc";

  // ① fonction de suppression
  const handleConfirmDelete = async () => {
    setDeleting(true);

    // 1) Supprimer toutes les pièces‑jointes
    setDeleteStatus(translate("DeletingAttachments")); // “Suppression des pièces jointes…”
    const folderRef = storageRef(storage, `tickets/${ticket.id}`);
    const listResult = await listAll(folderRef);
    await Promise.all(
      listResult.items.map((itemRef) => deleteObject(itemRef).catch(() => {}))
    );

    // 2) Supprimer la sous‑collection "activity"
    setDeleteStatus(translate("DeletingActivity")); // “Suppression de l’historique…”
    const activityCol = collection(
      db,
      "projects",
      selectedProject,
      "tickets",
      ticket.id,
      "activity"
    );
    const activitySnapshot = await getDocs(activityCol);
    await Promise.all(
      activitySnapshot.docs.map((docSnap) =>
        deleteDoc(docSnap.ref).catch(() => {})
      )
    );

    // 3) Supprimer le document ticket
    setDeleteStatus(translate("DeletingTicket")); // “Suppression du ticket…”
    await deleteDoc(doc(db, "projects", selectedProject, "tickets", ticket.id));

    // 4) Tout est fini
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Slide direction="left" in={open}>
          <Box
            sx={{
              position: "fixed",
              top: "5%",
              right: 0,
              width: "60%",
              height: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              marginRight: "15px",
              pl: "2",
              pr: "2",
              borderRadius: "15px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0,
                borderBottom: 1,
                borderColor: "divider",
                height: "8%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  id="edit-ticket-modal-title"
                  style={{
                    paddingLeft: "30px",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    fontSize: "22px",
                    fontWeight: "700",
                  }}
                >
                  {translate("UpdateTicket")}
                </div>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 3,
                    py: 1,
                    // borderBottom: "1px solid lightgray",
                    gap: 1,
                    marginBottom: "0px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      fontSize: 14,
                    }}
                  >
                    <strong>{translate("Creator")}:</strong>{" "}
                    {ticket.creatorName || ticket.creatorEmail}
                  </div>
                </Box>
              </div>

              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                height: "84%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: "50%",
                  flexDirection: "row",
                  // height: "100%",
                  paddingLeft: "30px",
                  paddingTop: "20px",
                  overflowY: "auto",
                }}
              >
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "20px",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div
                        style={{
                          fontWeight: "600",
                          paddingBottom: "10px",
                          fontSize: "16px",
                          paddingTop: "00px",
                          color: "#9d9e9e",
                        }}
                      >
                        {translate("TicketNumber")}
                      </div>
                      <div style={{ width: "100%" }}>
                        <Box
                          sx={{
                            mb: 2,
                            px: 2,
                            py: 1,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            fontSize: 16,
                            fontWeight: 500,
                            width: "fit-content",
                          }}
                        >
                          {ticket.ticketNumber}
                        </Box>
                      </div>
                    </div>

                    <div style={{ marginTop: "0px" }}>
                      <div
                        style={{
                          fontWeight: 600,
                          paddingBottom: "10px",
                          fontSize: "16px",
                          color: "#9d9e9e",
                        }}
                      >
                        {translate("Status")}
                      </div>

                      {role === "Admin" ? (
                        <Select
                          value={ticketStatus}
                          onChange={(e) => setTicketStatus(e.target.value)}
                          label={translate("Status")}
                          sx={{
                            bgcolor: currentStatusColor,
                            borderRadius: "40px",
                            height: "30px",
                            color: "#fff",
                            "& .MuiSelect-icon": { color: "#fff" },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: currentStatusColor,
                            },
                          }}
                        >
                          {statusOptions.map(({ value, label, color }) => (
                            <MenuItem
                              key={value}
                              // value={value}
                              // style={{ backgroundColor: color, color: "#fff" }}
                              value={value}
                              style={{
                                backgroundColor: statusColors[value] || "#ccc",
                                color: "#fff",
                              }}
                            >
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <div
                          style={{
                            paddingLeft: "10px",
                            paddingRight: "10px",
                            backgroundColor: currentStatusColor,
                            paddingTop: "5px",
                            borderRadius: "40px",
                            color: "#fff",
                            display: "inline-block",
                            fontWeight: 500,
                            height: "30px",
                            verticalAlign: "center",
                          }}
                        >
                          {statusOptions.find(
                            (opt) => opt.value === ticketStatus
                          )?.label || ticketStatus}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      fontWeight: "600",
                      color: "#9d9e9e",
                      paddingBottom: "5px",
                      fontSize: "16px",
                      paddingTop: "0px",
                    }}
                  >
                    {translate("TicketName")}
                  </div>
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        paddingBottom: "10px",
                        fontWeight: "600",
                        fontSize: "20px",
                        paddingRight: "10px",
                      }}
                    >
                      {ticketName}
                    </div>
                  </div>

                  <div
                    style={{
                      fontWeight: "600",
                      paddingBottom: "10px",
                      fontSize: "16px",
                      paddingTop: "10px",
                      color: "#9d9e9e",
                    }}
                  >
                    {translate("WriteDescription")}
                  </div>
                  <div style={{ width: "80%" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={originalQuestion}
                      onChange={(e) =>
                        status === "Not Seen Yet" &&
                        setOriginalQuestion(e.target.value)
                      }
                      disabled
                      multiline
                      rows={5}
                      sx={{
                        mb: 2, // replaces style={{ marginBottom: "20px" }}
                        // Root of the outlined input
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "background.paper", // ✅ themed bg (dark friendly)
                          color: "text.primary",
                          "& fieldset": { borderColor: "divider" },
                          "&:hover fieldset": { borderColor: "divider" },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                        // Text color for the input/textarea
                        "& .MuiInputBase-input": {
                          color: "text.primary",
                        },
                        // MUI forces a light grey text color when disabled; reset it
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "inherit",
                          opacity: 1,
                        },
                        // Ensure the textarea part (multiline) follows colors
                        "& textarea": {
                          color: "text.primary",
                        },
                      }}
                    />
                  </div>

                  <div style={{ paddingTop: "0px" }}>
                    <div
                      style={{
                        fontWeight: "600",
                        paddingBottom: "10px",
                        fontSize: "16px",
                        color: "#9d9e9e",
                      }}
                    >
                      {translate("Priority")}
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                      {["Low", "Medium", "High"].map((level) => {
                        const bg = PRIORITY_BG[level];
                        const fg = theme.palette.getContrastText(bg);
                        const isSelected = selectedPriority === level;
                        // const isDisabled = role === "Admin";
                        const isDisabled = false;
                        return (
                          <Box
                            key={level}
                            onClick={() =>
                              !isDisabled && setSelectedPriority(level)
                            }
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              px: 2,
                              py: 1,
                              borderRadius: "20px",
                              bgcolor: bg,
                              color: fg, // ✅ texte toujours lisible
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.6 : 1,
                              // border: isSelected
                              //   ? `2px solid ${fg}`
                              //   : "1px solid transparent",
                              border: "1px solid transparent",
                              boxShadow: isSelected
                                ? `0 0 0 2px ${fg} inset, 0 0 0 2px ${fg}`
                                : "none",
                              fontWeight: isSelected ? 700 : 500,
                              userSelect: "none",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: fg, // petit point contrasté
                              }}
                            />
                            <Typography variant="body2" sx={{ m: 0 }}>
                              {translate(level)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "40px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingTop: "15px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          paddingBottom: "5px",
                          fontSize: "16px",
                          paddingTop: "10px",
                          color: "#9d9e9e",
                        }}
                      >
                        {translate("TicketType")}
                      </div>

                      {role === "Admin" ? (
                        <Box
                          sx={{
                            px: 2,
                            py: 1,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            fontSize: 18,
                            fontWeight: 500,
                            width: "fit-content",
                          }}
                        >
                          {ticketType}
                        </Box>
                      ) : (
                        <>
                          <div style={{ fontSize: "18px", fontWeight: "600" }}>
                            {ticketType}
                          </div>
                        </>
                      )}
                    </div>

                    <div
                      style={{ display: "flex", flexDirection: "column" }}
                    ></div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingTop: "15px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        paddingBottom: "0px",
                        fontSize: "16px",
                        paddingTop: "10px",
                        color: "#9d9e9e",
                      }}
                    >
                      {translate("AttachedFiles")}
                    </div>

                    <Box sx={{ my: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setUploadModalOpen(true)}
                      >
                        {translate("ManageFile")}
                      </Button>

                      {localAttachments.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mt: 1,
                            borderRadius: "5px",
                            border: "1px dashed lightgrey",
                            bgcolor: "action.hover",
                            p: 1,
                            overflowX: "auto", // ← scroll horizontal
                            whiteSpace: "nowrap", // ← empêcher le wrap
                            // on enlève flexWrap
                          }}
                        >
                          {localAttachments.map((a, i) => (
                            <div
                              style={{
                                backgroundColor: "#eaeaea",
                                paddingLeft: "5px",
                                paddingRight: "5px",
                                paddingTop: "5px",
                                paddingBottom: "5px",
                                borderRadius: "5px",
                              }}
                            >
                              <IconButton
                                key={i}
                                component="a"
                                href={a.file ? a.preview : a.url}
                                target="_blank"
                                sx={{ p: 0 }}
                              >
                                <Avatar
                                  variant="rounded"
                                  src={a.preview || a.url}
                                  sx={{ width: 56, height: 56 }}
                                />
                              </IconButton>
                            </div>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* MODAL D'UPLOAD */}
                    <UploadFilesModal
                      open={uploadModalOpen}
                      onClose={() => setUploadModalOpen(false)}
                      files={localAttachments}
                      setFiles={setLocalAttachments}
                    />
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  width: "50%",
                  paddingLeft: "20px",
                }}
              >
                <div style={{ width: "100%" }}>
                  <TicketActivityHistory
                    newComment={newComment}
                    setNewComment={setNewComment}
                    currentUser={currentUser}
                    ticket={ticket}
                    role={role}
                    selectedProject={selectedProject}
                    translate={translate}
                  />
                </div>
              </div>
            </div>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 0,
                paddingLeft: "30px",
                paddingRight: "30px",
                paddingBottom: "10px",
                paddingTop: "10px",
                borderTop: 1,
                borderColor: "divider",
                height: "8%",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                color="error"
                variant="contained"
                onClick={() => setOpenDeleteConfirm(true)}
                sx={{ mr: 2 }}
              >
                {" "}
                {translate("DeleteTicket")}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                style={{ marginRight: "10px", height: "40px" }}
                onClick={onClose}
              >
                {translate("Cancel")}
              </Button>

              <Button
                onClick={() => {
                  const payload = { images: localAttachments };
                  if (role === "Admin") payload.status = ticketStatus;
                  handleUpdate(payload);
                }}
                variant="contained"
                color={hasChanges ? "error" : "primary"}
                disabled={loading}
                sx={{
                  height: "40px",
                  // si des changements, on anime en rouge
                  ...(hasChanges && {
                    animation: "blink 4s linear infinite",
                    "@keyframes blink": {
                      "0%, 20%": { opacity: 1 },
                      "30%, 50%": { opacity: 0 },
                      "60%, 100%": { opacity: 1 },
                    },
                  }),
                }}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : role === "Admin" ? (
                  translate("UpdateTicket")
                ) : (
                  translate("SaveAttachments")
                )}
              </Button>
            </Box>
          </Box>
        </Slide>
      </Modal>
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
      >
        <DialogTitle>{translate("ConfirmDeleteTicket")}</DialogTitle>

        {/* Affiche l’étape en cours */}
        {deleting && (
          <Box px={3} pb={1}>
            <Typography variant="body2" color="textSecondary">
              {deleteStatus}
            </Typography>
          </Box>
        )}

        <DialogActions>
          <Button
            onClick={() => setOpenDeleteConfirm(false)}
            disabled={deleting}
          >
            {translate("Cancel")}
          </Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? translate("Deleting") : translate("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditTicketModal;
